<template>
  <section
    ref="videoHeroSection"
    class="video-hero relative w-full h-screen overflow-hidden"
    style="min-height: 100vh;"
  >
    <div class="video-bg">
              <iframe
          ref="heroVideo"
          id="vimeo-player"
          src="https://player.vimeo.com/video/1118778747?background=1&autoplay=1&loop=1&byline=0&title=0&muted=0&controls=0&portrait=0&sidedock=0&dnt=1&transparent=0&volume=1"
          frameborder="0"
          allow="autoplay; fullscreen; picture-in-picture"
        ></iframe>
    </div>

    <div class="absolute inset-0 bg-black bg-opacity-30 z-10"></div>

    <div class="relative z-20 flex items-center justify-center h-full px-4 sm:px-6 lg:px-8">
      <div class="text-center w-full max-w-4xl mx-auto">
        <h1
          v-if="title"
          class="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-light font-hanken mb-4 sm:mb-6 opacity-0 animate-fade-in-up break-words leading-tight"
          :class="{ 'animate-fade-in-up-active': isLoaded }"
        >
          {{ title }}
        </h1>
        <p
          v-if="subtitle"
          class="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-light font-hanken max-w-xs sm:max-w-md md:max-w-lg lg:max-w-2xl mx-auto opacity-0 animate-fade-in-up-delay break-words leading-relaxed px-2"
          :class="{ 'animate-fade-in-up-active': isLoaded }"
        >
          {{ subtitle }}
        </p>
      </div>
    </div>

    <!-- Botão de som on/off centralizado embaixo -->
    <div class="absolute bottom-24 left-0 right-0 flex justify-center" style="z-index: 2147483647 !important; position: relative !important;">
      <div
        @click="toggleSound"
        class="sound-toggle-text text-white cursor-pointer transition-all duration-300 ease-in-out"
        style="z-index: 2147483647 !important; position: relative !important;"
        :title="isMuted ? 'Sound On' : 'Sound Off'"
      >
        <!-- Texto do som -->
        <span class="text-xs sm:text-sm md:text-base font-light whitespace-nowrap">
          SOUND 
          <span :class="{ 'underline': isMuted }">{{ $t('sound.off') }}</span> / 
          <span :class="{ 'underline': !isMuted }">{{ $t('sound.on') }}</span>
        </span>
      </div>
    </div>

    <!-- Seta para baixo -->
    <div class="absolute bottom-4 left-0 right-0 flex justify-center z-40">
      <div class="animate-bounce">
        <svg
          class="w-6 h-6 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </div>
    </div>
  </section>
</template>

<script>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Player from '@vimeo/player'

// Registrar o plugin ScrollTrigger
gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'VideoHero',
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
  },
  emits: ['video-loaded'],
  data() {
    return {
      isLoaded: false,
      intersectionObserver: null,
      scrollTrigger: null,
      effectDifferenceTimeline: null,
      effectDifferenceRemoved: false,
      isMobile: false,
      currentBreakpoint: 'desktop',
      isMuted: false,
      vimeoPlayer: null
    }
  },
  mounted() {
    this.checkDevice()
    this.setupIntersectionObserver()
    this.setupVideoLoading()

    // Só configurar ScrollTrigger se não for mobile
    if (!this.isMobile) {
      this.setupScrollTrigger()
    }

    // Listener para redimensionamento da janela
    window.addEventListener('resize', this.handleResize)
    
    // Listener para evento de toggle do som disparado pelo App.vue
    window.addEventListener('toggle-video-sound', this.toggleSound)

    // Ativar animações após um delay
    setTimeout(() => {
      this.isLoaded = true
    }, 500)


    // Garantir estado inicial correto após um delay maior
    setTimeout(() => {
      // Se ainda não temos o player e o estado está incorreto, corrigir
      if (!this.vimeoPlayer && this.isMuted) {
        this.isMuted = false
      }
      
      // Tentar forçar desmutar uma última vez
      const iframe = this.$refs.heroVideo
      if (iframe) {
        iframe.contentWindow.postMessage({
          method: 'setMuted',
          value: false
        }, 'https://player.vimeo.com')
        
        iframe.contentWindow.postMessage({
          method: 'setVolume',
          value: 1
        }, 'https://player.vimeo.com')
      }
    }, 2000)
  },
  beforeUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
    if (this.scrollTrigger) {
      this.scrollTrigger.kill()
    }
    if (this.effectDifferenceTimeline) {
      this.effectDifferenceTimeline.kill()
    }
    if (this.vimeoPlayer) {
      this.vimeoPlayer.destroy()
    }

    // Remover listeners
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('toggle-video-sound', this.toggleSound)
  },
  methods: {
    checkDevice() {
      const width = window.innerWidth
      
      // Definir breakpoints mais granulares
      if (width <= 640) {
        this.currentBreakpoint = 'mobile'
        this.isMobile = true
      } else if (width <= 768) {
        this.currentBreakpoint = 'sm'
        this.isMobile = true
      } else if (width <= 1024) {
        this.currentBreakpoint = 'md'
        this.isMobile = false
      } else if (width <= 1280) {
        this.currentBreakpoint = 'lg'
        this.isMobile = false
      } else {
        this.currentBreakpoint = 'xl'
        this.isMobile = false
      }

      // Se for mobile/tablet, configurar header fixo imediatamente
      if (this.isMobile) {
        this.setupMobileHeader()
      }
    },

    setupMobileHeader() {
      const app = this.$root
      if (!app) return

      // Configurar header fixo para mobile/tablet com valores ajustados por breakpoint
      app.headerHeight = 80
      
      // Ajustar tamanho do logo baseado no breakpoint
      if (this.currentBreakpoint === 'mobile') {
        app.logoWidth = 80 // Menor para mobile
      } else if (this.currentBreakpoint === 'sm') {
        app.logoWidth = 90 // Ligeiramente maior para tablets pequenos
      }
      
      app.textSize = 12
      app.textPosition = 50
      app.isOverVideo = false
      app.progress = 1
      app.showEffectDifference = false
    },

          handleResize() {
        const wasBreakpoint = this.currentBreakpoint
        const wasMobile = this.isMobile
        
        this.checkDevice()

        // Se mudou de desktop para mobile/tablet
        if (!wasMobile && this.isMobile) {
          this.setupMobileHeader()
          if (this.scrollTrigger) {
            this.scrollTrigger.kill()
            this.scrollTrigger = null
          }
        }
        // Se mudou de mobile/tablet para desktop
        else if (wasMobile && !this.isMobile) {
          if (!this.scrollTrigger) {
            this.setupScrollTrigger()
          }
        }
        // Se mudou apenas entre breakpoints mobile/tablet
        else if (this.isMobile && wasBreakpoint !== this.currentBreakpoint) {
          this.setupMobileHeader()
        }
        
        // Atualizar valores iniciais baseado no novo breakpoint
        this.updateInitialValues()
      },

    setupIntersectionObserver() {
      // Observer para controlar visibilidade do iframe (performance)
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            // Para iframe do Vimeo, não precisamos controlar play/pause
            // O Vimeo já gerencia isso automaticamente com os parâmetros de background
            // if (entry.isIntersecting) {
            //   console.log('Video section is visible')
            // } else {
            //   console.log('Video section is not visible')
            // }
          })
        },
        { threshold: 0.1 }
      )

      this.intersectionObserver.observe(this.$el)
    },

    setupVideoLoading() {
      // Detectar quando o iframe do Vimeo está carregado
      const iframe = this.$refs.heroVideo
      if (iframe) {
        iframe.addEventListener('load', () => {
          // Inicializar o player do Vimeo
          this.initializeVimeoPlayer()
          
          // Garantir estado inicial correto mesmo se a API falhar
          setTimeout(() => {
            if (!this.vimeoPlayer) {
              // Se a API falhou, garantir que o estado visual esteja correto
              this.isMuted = false
              
              // Tentar forçar desmutar via postMessage como fallback
              const iframe = this.$refs.heroVideo
              if (iframe) {
                iframe.contentWindow.postMessage({
                  method: 'setMuted',
                  value: false
                }, 'https://player.vimeo.com')
                
                iframe.contentWindow.postMessage({
                  method: 'setVolume',
                  value: 1
                }, 'https://player.vimeo.com')
              }
            }
          }, 1000)
          
          // Emitir evento para o App.vue
          this.$emit('video-loaded', 'hero')
        })
        
        // Fallback: se o iframe já estiver carregado
        if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
          this.initializeVimeoPlayer()
          
          // Garantir estado inicial correto
          setTimeout(() => {
            if (!this.vimeoPlayer) {
              this.isMuted = false
            }
          }, 1000)
          
          this.$emit('video-loaded', 'hero')
        }
      }
    },

    async initializeVimeoPlayer() {
      try {
        // Tentar inicializar o player do Vimeo
        this.vimeoPlayer = new Player('vimeo-player')
        
        // Aguardar o player estar pronto
        await this.vimeoPlayer.ready()
        
        // Forçar desmutar e definir volume
        await this.vimeoPlayer.setMuted(false)
        await this.vimeoPlayer.setVolume(1)
        this.isMuted = false
        
      } catch (error) {
        // Erro esperado em ambiente local (CORS). Usar fallback com postMessage
        this.vimeoPlayer = null
        this.setupPostMessageListener()
        // console.log('VideoHero: Usando fallback com postMessage')
      }
    },

    setupPostMessageListener() {
      // Configurar listener para mensagens do iframe do Vimeo
      window.addEventListener('message', (event) => {
        // Verificar se a mensagem é do Vimeo
        if (event.origin !== 'https://player.vimeo.com') return
        
        if (event.data && event.data.event === 'ready') {
          // Garantir que o estado visual esteja correto
          this.isMuted = false
          
          // Tentar forçar desmutar via postMessage
          const iframe = this.$refs.heroVideo
          if (iframe) {
            iframe.contentWindow.postMessage({
              method: 'setMuted',
              value: false
            }, 'https://player.vimeo.com')
            
            iframe.contentWindow.postMessage({
              method: 'setVolume',
              value: 1
            }, 'https://player.vimeo.com')
            
          }
        }
      })
    },

    async toggleSound() {
      // Se a API do Vimeo Player estiver disponível, usar ela
      if (this.vimeoPlayer) {
        try {
          if (this.isMuted) {
            // Desmutar o vídeo
            await this.vimeoPlayer.setMuted(false)
            this.isMuted = false
          } else {
            // Mutar o vídeo
            await this.vimeoPlayer.setMuted(true)
            this.isMuted = true
          }
          return
        } catch (error) {
          // Continuar para o fallback
        }
      }

      // Tentar usar postMessage para controlar o iframe
      const iframe = this.$refs.heroVideo
      if (iframe) {
        try {
          // Enviar comando para o iframe do Vimeo
          iframe.contentWindow.postMessage({
            method: this.isMuted ? 'setMuted' : 'setMuted',
            value: this.isMuted ? false : true
          }, 'https://player.vimeo.com')
          
          this.isMuted = !this.isMuted
          return
        } catch (error) {
          // Fallback para URL (reinicia o vídeo)
        }
      }

      // Fallback final: usar modificação da URL (reinicia o vídeo)
      if (iframe) {
        const currentSrc = iframe.src
        
        if (this.isMuted) {
          // Desmutar: trocar muted=1 para muted=0
          const newSrc = currentSrc.replace('muted=1', 'muted=0')
          iframe.src = newSrc
          this.isMuted = false
        } else {
          // Mutar: trocar muted=0 para muted=1
          const newSrc = currentSrc.replace('muted=0', 'muted=1')
          iframe.src = newSrc
          this.isMuted = true
        }
      }
    },

    setupScrollTrigger() {
      // Configurar ScrollTrigger para controlar o scroll
      this.scrollTrigger = ScrollTrigger.create({
        trigger: this.$refs.videoHeroSection,
        start: 'top top',
        end: 'bottom top',
        pin: true,
        pinSpacing: true,
        onUpdate: (self) => {
          // Animar o header baseado no scroll real
          this.animateHeaderFromScroll()
        }
      })
    },

    animateHeaderFromScroll() {
      // Não animar se for mobile/tablet
      if (this.isMobile) return

      const app = this.$root
      if (!app) return

      // Usar requestAnimationFrame para melhor performance
      requestAnimationFrame(() => {
        // Usar o scroll real para calcular o progresso
        const scrollY = window.scrollY
        const maxScroll = window.innerHeight * 0.6
        const progress = Math.min(scrollY / maxScroll, 1)

        // Função de easing mais suave para fluidez
        const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4)
        const easedProgress = easeOutQuart(progress)

        // Animar altura do header com valores mais precisos
        const initialHeight = window.innerHeight * 1
        const finalHeight = 80
        const headerHeight = initialHeight - (easedProgress * (initialHeight - finalHeight))

        // Animar largura do logo com ajuste baseado no breakpoint
        let initialLogoWidth, finalLogoWidth
        
        if (this.currentBreakpoint === 'md') {
          initialLogoWidth = 320
          finalLogoWidth = 100
        } else if (this.currentBreakpoint === 'lg') {
          initialLogoWidth = 350
          finalLogoWidth = 110
        } else { // xl e maiores
          initialLogoWidth = 380
          finalLogoWidth = 116 * 0.9
        }
        
        const logoWidth = initialLogoWidth - (easedProgress * (initialLogoWidth - finalLogoWidth))

        // Animar tamanho do texto
        const initialTextSize = 24
        const finalTextSize = 12
        const textSize = initialTextSize - (easedProgress * (initialTextSize - finalTextSize))

        // Animar posição do texto baseado no breakpoint
        let initialTextPosition
        
        if (this.currentBreakpoint === 'md') {
          initialTextPosition = 87 // Menor para tablets
        } else if (this.currentBreakpoint === 'lg') {
          initialTextPosition = 92 // Médio para desktop pequeno
        } else { // xl e maiores
          initialTextPosition = 92 // Máximo para telas grandes
        }
        
        const finalTextPosition = 50
        const textPosition = initialTextPosition - (easedProgress * (initialTextPosition - finalTextPosition))

        // Atualizar valores no App.vue diretamente para máxima fluidez
        app.headerHeight = headerHeight
        app.logoWidth = logoWidth
        app.textSize = textSize
        app.textPosition = textPosition
        app.progress = progress
        app.isOverVideo = progress < 1

        // Controlar effect-difference com GSAP baseado no progresso
        this.controlEffectDifferenceWithGSAP(progress)
      })
    },

    controlEffectDifferenceWithGSAP(progress) {
      // Não controlar effect-difference se for mobile/tablet
      if (this.isMobile) return

      const app = this.$root
      if (!app || !app.$refs.header) return

      const header = app.$refs.header
      const videoSection = this.$refs.videoHeroSection

      // Calcular se o header está sobrepondo o vídeo
      const headerRect = header.getBoundingClientRect()
      const videoRect = videoSection.getBoundingClientRect()

      // Header está sobrepondo o vídeo se qualquer parte do header está dentro da área do vídeo
      const isHeaderOverVideo = headerRect.bottom > videoRect.top && headerRect.top < videoRect.bottom

      // Se não existe timeline para effect-difference, criar uma
      if (!this.effectDifferenceTimeline) {
        this.effectDifferenceTimeline = gsap.timeline({ paused: true })

        // Timeline para remover a classe effect-difference quando header sair do vídeo
        this.effectDifferenceTimeline
          .to(header, {
            duration: 0.3,
            onStart: () => {
              // Remover a classe quando a animação começar
              app.showEffectDifference = false
              this.effectDifferenceRemoved = true
            },
            onReverseComplete: () => {
              // Restaurar a classe quando a animação reverter completamente
              app.showEffectDifference = true
              this.effectDifferenceRemoved = false
            },
            ease: "power2.out"
          })
      }

      // Controlar a timeline baseado na sobreposição com o vídeo
      if (!isHeaderOverVideo && !this.effectDifferenceRemoved) {
        // Iniciar animação de remoção da classe quando header sair do vídeo
        this.effectDifferenceTimeline.play()
      } else if (isHeaderOverVideo && this.effectDifferenceRemoved) {
        // Reverter a animação se o header voltar a sobrepor o vídeo
        this.effectDifferenceTimeline.reverse()
      }
    },
    
    updateInitialValues() {
      const app = this.$root
      if (!app) return
      
      // Atualizar valores iniciais baseado no breakpoint atual
      if (this.currentBreakpoint === 'md') {
        app.textPosition = 87
      } else if (this.currentBreakpoint === 'lg') {
        app.textPosition = 92
      } else { // xl e maiores
        app.textPosition = 92
      }
    },

  }
}
</script>

<style scoped>
.video-hero {
  position: relative;
  height: 100vh;
  min-height: 100vh;
  z-index: 1;
}

/* Animações customizadas */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation-duration: 0.8s;
  animation-fill-mode: both;
  animation-timing-function: ease-out;
}

.animate-fade-in-up-active {
  animation-name: fadeInUp;
}

.animate-fade-in-up-delay {
  animation-delay: 0.3s;
}

/* Video background styles */
.video-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.video-bg iframe {
  width: 100vw;
  height: 56.25vw; /* 16:9 aspect ratio */
  min-height: 100%;
  min-width: 177.77vh; /* 16:9 aspect ratio */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Otimização para diferentes breakpoints */
@media (max-width: 640px) {
  .video-hero {
    height: 100vh;
    height: 100svh; /* Para suporte a Safari mobile */
  }
  
  /* Ajustes específicos para mobile muito pequeno */
  .video-hero h1 {
    line-height: 1.1;
  }
  
  .video-hero p {
    line-height: 1.4;
  }
}

@media (min-width: 641px) and (max-width: 768px) {
  /* Ajustes para tablets pequenos */
  .video-hero h1 {
    line-height: 1.2;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  /* Ajustes para tablets grandes / desktop pequeno */
  .video-hero h1 {
    line-height: 1.15;
  }
}

/* Texto de som */
.sound-toggle-text {
  transition: all 0.3s ease-in-out;
  z-index: 2147483647 !important;
  position: relative !important;
  white-space: nowrap;
}

.sound-toggle-text:hover {
  opacity: 0.8;
}

/* Scroll indicator animation */
.animate-bounce {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
    transform: translate3d(0, -8px, 0);
  }
  70% {
    animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
    transform: translate3d(0, -4px, 0);
  }
  90% {
    transform: translate3d(0,-2px,0);
  }
}</style>